import * as React from "react";
import { useState } from "react";
import {
  faCalendarDays,
  faFilter,
  faFilterSlash,
  faSearch,
} from "@fortawesome/sharp-regular-svg-icons";
import { faCircleInfo } from "@fortawesome/pro-solid-svg-icons";
import { library } from "@fortawesome/fontawesome-svg-core";
import Tooltip from "@mui/material/Tooltip";
import {
  Autocomplete,
  BackendFailureAlert,
  Box,
  Button,
  Checkbox,
  CheckBoxIcon,
  CheckBoxOutlineBlankIcon,
  Divider,
  focusRing,
  Grid2 as Grid,
  LinearProgress,
  Paper,
  pxToRem,
  TextField,
  Typography,
} from "@platform/style";
import { WorkGroup } from "../../../models/WorkGroup";
import { TemplateV2 } from "../../../models/TemplateV2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPeopleGroup } from "@fortawesome/sharp-solid-svg-icons";

library.add(faFilter, faCalendarDays, faFilterSlash, faSearch);

interface WorkGroupConfigurationProps {
  onHandleCreate: () => void;
  onHandleBack: () => void;
  onHandleNext: () => void;
  isLoadingGroups: boolean;
  isErrorGroups: boolean;
  refetchGroups: () => void;
  onGroupUpdate: any;
  groups: WorkGroup[];
  selectedTemplate?: TemplateV2;
}

export default function WorkGroupConfiguration(props: Readonly<WorkGroupConfigurationProps>) {
  const workGroups = props.groups.filter((group: { WorkGroupIdentifier: string }) =>
    props.selectedTemplate?.WorkGroups.some(
      (oldGroup) => oldGroup.WorkGroupIdentifier === group.WorkGroupIdentifier
    )
  );

  const [groups, setGroups] = useState(workGroups);

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  const handleChange = (_event: any, newValue: any) => {
    setGroups(newValue);
  };

  function getSelectedGroups() {
    props.onGroupUpdate(groups);
  }

  const handleNext = () => {
    getSelectedGroups();
    props.onHandleNext();
  };

  const [tooltipOpen, setTooltipOpen] = useState(false);

  return (
    <>
      <Typography
        aria-label={"Group Configuration Header"}
        variant="h6"
        sx={{ paddingTop: "20px", paddingBottom: "7px", paddingLeft: "3px", textAlign: "center" }}
      >
        Configure a Work Group for this Template
        <Typography>
          {groups.length === 0 ? (
            <FontAwesomeIcon
              data-testid={"info-icon"}
              onMouseEnter={() => setTooltipOpen(true)}
              onMouseLeave={() => setTooltipOpen(false)}
              icon={faCircleInfo}
              color={"#235f73"}
            />
          ) : null}
          &nbsp;Choose an existing group or create a new group.
        </Typography>
      </Typography>
      <Paper
        elevation={0}
        variant="outlined"
        min-height="100vh"
        sx={{
          backgroundColor: "inherit",
          display: "flex",
          flexDirection: "column",
          border: "none",
          paddingLeft: "8px",
          paddingRight: "8px",
        }}
      >
        <Grid
          container
          sx={{
            mt: 4,
            justifyContent: "center",
            alignItems: "center",
            paddingBottom: "20px",
          }}
        >
          {props.isLoadingGroups ? (
            <LinearProgress sx={{ mt: 1 }} />
          ) : (
            <Box sx={{ mt: 1, height: "4px" }}></Box>
          )}
          {props.isErrorGroups ? (
            <Box sx={{ mt: 1 }}>
              <BackendFailureAlert retryFunction={props.refetchGroups} />
            </Box>
          ) : (
            <Grid size={{ sm: 5, xs: 11 }} sx={{ height: "30vh" }}>
              <Typography style={{ fontWeight: "bold", textAlign: "center", paddingBottom: "8px" }}>
                Select from existing group
              </Typography>
              <Autocomplete
                id="addGroups"
                multiple
                defaultValue={workGroups}
                noOptionsText={"No Work Groups"}
                disableCloseOnSelect
                limitTags={5}
                options={props.groups}
                getOptionLabel={(option) => option.Name}
                onChange={handleChange}
                renderInput={(params) => (
                  <TextField {...params} label="Group Search" placeholder="Select a Group(s)" />
                )}
                renderOption={(props, option, { selected }) => {
                  const { key, ...optionProps } = props;
                  return (
                    <li key={key} {...optionProps}>
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 8 }}
                        checked={selected}
                      />
                      {option.Name}{" "}
                      {option?.Resources?.length > 1
                        ? "- " + option?.Resources?.length + " People"
                        : "- " + option?.Resources?.length + " Person"}
                    </li>
                  );
                }}
              />
            </Grid>
          )}
          <Divider
            orientation="vertical"
            sx={{
              marginLeft: "auto",
              marginRight: "auto",
              backgroundColor: "#e8e8e8",
              borderColor: "#e8e8e8",
              borderRadius: "10px",
              borderWidth: "5px",
            }}
            flexItem
          />
          <Grid size={{ sm: 5, xs: 11 }} sx={{ height: "30vh" }}>
            <Box
              style={{
                display: "grid",
                alignItems: "center",
              }}
            >
              <Typography
                style={{
                  fontWeight: "bold",
                  textAlign: "center",
                  paddingBottom: "8px",
                }}
              >
                Create a new group
              </Typography>
              <Button
                startIcon={<FontAwesomeIcon icon={faPeopleGroup} />}
                content={"Create New Group"}
                id="create-group"
                aria-label={"Create Work Group"}
                onClick={props.onHandleCreate}
                sx={{ backgroundColor: "rgb(113, 197, 232)" }}
                variant={"contained"}
                color="secondary"
              >
                Create New Group
              </Button>
            </Box>
          </Grid>
          <Grid container sx={{ mt: 2, justifyContent: "flex-end" }}>
            <Box
              sx={{
                bottom: "60px",
                display: "flex",
                flexDirection: "row",
                pt: 2,
              }}
            >
              <Button
                aria-label={"Group Configuration Back"}
                variant="outlined"
                color={"primary"}
                sx={{ focusRing, marginRight: "5px" }}
                onClick={props.onHandleBack}
              >
                Back
              </Button>
              <Box sx={{ flex: "1 1 auto" }} />
              <Tooltip
                title={"Choose an existing group or create a new group to continue."}
                arrow
                placement="top"
                open={tooltipOpen}
                slotProps={{
                  popper: {
                    sx: {
                      display: groups.length !== 0 ? "none" : undefined,
                      maxWidth: pxToRem(150),
                      textAlign: "center",
                      paddingBottom: pxToRem(7),
                      fontSize: pxToRem(12),
                    },
                  },
                }}
              >
                <Box
                  data-testid={"tooltip-hover-region"}
                  onMouseEnter={() => setTooltipOpen(true)}
                  onMouseLeave={() => setTooltipOpen(false)}
                >
                  <Button
                    aria-label={"Group Configuration Next"}
                    variant="contained"
                    color={"primary"}
                    sx={focusRing}
                    disabled={props.isLoadingGroups || props.isErrorGroups || groups.length === 0}
                    onClick={handleNext}
                  >
                    Next
                  </Button>
                </Box>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
}
